import React, { ChangeEvent, useEffect, useState } from 'react';
import { Box, Grid, TextField, Typography, useMediaQuery } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { SymbolList } from './components/SymbolList';
import { useStore } from '@cobuildlab/react-simple-state';
import { changeStrategyStore } from '../strategy/strategy-events';
import { ButtonDefault } from '../../shared/components/ButtonDefault';
import { StrategyTime } from './components/StrategyTime';
import { Loading } from '../../shared/components/Loading';
import { useTheme } from '@mui/material/styles';
import {
  ConfigurationStrategy,
  SortOrder, StrategiesDocument,
  Strategy,
  StrategyHistory,
  StrategyStatus, useConfigurationStrategiesAndStrategiesLazyQuery,
  useStrategyHistoriesLazyQuery, useUpdateConfigurationStrategyAndStrategyMutation,
} from '../../shared/types/generated';
import { changeStrategyAction } from '../strategy/strategy-actions';
import { StrategyHistoryItem } from './components/StrategyHistoryItem';
import { openSnackbarAction } from '../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../shared/components/snackbar/snackbar-types';

export const DEFAULT_STRATEGY_VALUE = {
  id: '',
  symbol: 'AUDJPY',
  favor_movement: 0,
  against_movement: 0,
  red_against: 0,
  safety_loss: 0,
  safety_profit: 0,
  cycles: 0,
};

export const DEFAULT_CONFIGURATION_STRATEGY_VALUE = {
  id: '',
  max_day_profit: 0,
  min_day_profit: 0,
};

export const SettingStrategy: React.FC = () => {
  const { strategy } = useStore(changeStrategyStore);
  const [selected, setSelected] = useState<Strategy>(DEFAULT_STRATEGY_VALUE);
  const [configurationSelected, setConfigurationSelected] = useState<ConfigurationStrategy>(DEFAULT_CONFIGURATION_STRATEGY_VALUE);

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const [callStrategies, {
    loading: loadingStrategies,
    data: strategiesData,
  }] = useConfigurationStrategiesAndStrategiesLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const currentStrategy = data.strategies?.items?.find((item) => item?.id === strategy?.id);

      const currentConfigurationStrategy = data?.configurationStrategies;
      if (currentConfigurationStrategy) {
        setConfigurationSelected({
          id: currentConfigurationStrategy.id,
          min_day_profit: currentConfigurationStrategy.min_day_profit || 0,
          max_day_profit: currentConfigurationStrategy.max_day_profit,
        });
      }


      if (!currentStrategy) {
        const defaultStrategy = data.strategies?.items?.length
          ? data.strategies?.items[0]
          : undefined;
        changeStrategyAction(defaultStrategy);
      } else {
        changeStrategyAction(currentStrategy);
      }
    },
  });

  const [callStrategyHistories, {
    loading: loadingStrategyHistories,
    data: strategyHistoriesData,
  }] = useStrategyHistoriesLazyQuery();


  useEffect(() => {
    callStrategies({
      variables: {
        where: {
          status: StrategyStatus.Active,
        },
      },
    });
  }, [callStrategies]);

  useEffect(() => {
    if (strategy?.id) {
      callStrategyHistories({
        variables: {
          where: {
            strategy: {
              id: {
                equals: strategy.id,
              },
            },
          },
          orderBy: [{ created_at: SortOrder.Desc }],
        },
      });

      setSelected(strategy);
    }
  }, [callStrategyHistories, strategy]);

  const [callUpdate, { loading: updateLoading }] = useUpdateConfigurationStrategyAndStrategyMutation({
    awaitRefetchQueries: true,
    refetchQueries: [StrategiesDocument],
    onCompleted: () => {
      openSnackbarAction('Update strategy successfully!', TypeMessage.SUCCESS);
      callStrategies({
        variables: {
          where: {
            status: StrategyStatus.Active,
          },
        },
      });
    },
  });

  const onChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setSelected({
      ...selected,
      [event.target.name as keyof Strategy]: event.target.value,
    });
  };


  const onChangeConfigurationValue = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setConfigurationSelected({
      ...configurationSelected,
      [event.target.name as keyof ConfigurationStrategy]: event.target.value,
    });
  };

  if (loadingStrategies) return <Loading height='100%' />;

  const button = (
    <Box display={'flex'} alignItems={'center'}>
      <ButtonDefault
        isLoading={updateLoading}
        onClick={() => {
          const { id } = configurationSelected;
          console.log({ configurationSelected });
          callUpdate({
            variables: {
              data: {
                favor_movement: parseFloat(selected.favor_movement?.toString() as string),
                safety_profit: parseFloat(selected?.safety_profit?.toString() as string),
                safety_loss: parseFloat(selected?.safety_loss?.toString() as string),
                red_against: parseFloat(selected?.red_against?.toString() as string),
                against_movement: parseFloat(selected?.against_movement?.toString() as string),
                cycles: parseInt(selected.cycles?.toString() as string),
              },
              where: {
                id: strategy?.id,
              },
              data2: {
                max_day_profit: parseInt(configurationSelected.max_day_profit?.toString() as string),
                min_day_profit: parseInt(configurationSelected.min_day_profit?.toString() as string),
              },
              where2: {
                id,
              },
            },
          });
        }

        }
        style={{
          height: 40,
          textTransform: 'capitalize',
          background: '#D2FFCE',
          color: '#5D8F5A',
        }}
      >
        Save
      </ButtonDefault>
      <Box paddingLeft={1} display={'flex'} alignItems={'center'}>
        <AssignmentIcon fontSize='small' />
      </Box>
    </Box>
  );

  const textMediaQuery = isMd ? 5 : 4;
  const boxMediaQuery = isMd ? 7 : 8;
  const strategies = (strategiesData?.strategies?.items || []) as Strategy[];
  const strategyHistories = (strategyHistoriesData?.strategyHistories?.items || []) as StrategyHistory[];

  return (
    <Box>
      <Grid container borderBottom={'1px solid #F0F0F0'} spacing={1}>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
              <Grid container>
                <Grid display='flex' alignItems='center' item xs={textMediaQuery}>
                  <Typography paddingRight={1}>Min Daily Profit</Typography>
                </Grid>
                <Grid item xs={boxMediaQuery}>
                  <TextField
                    name='min_day_profit'
                    fullWidth
                    color={'success'}
                    size='small'
                    value={configurationSelected?.min_day_profit}
                    inputProps={{
                      style: {
                        paddingRight: '20px',
                        paddingLeft: '1px!important',
                      },
                    }}
                    onChange={onChangeConfigurationValue}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container>
                <Grid item display='flex' alignItems='center' xs={textMediaQuery}>
                  <Typography paddingRight={1}>Max Daily Loss</Typography>
                </Grid>
                <Grid item xs={boxMediaQuery}>
                  <TextField
                    name='max_day_profit'
                    fullWidth
                    color={'success'}
                    size='small'
                    value={configurationSelected?.max_day_profit}
                    inputProps={{
                      style: {
                        paddingRight: '20px',
                        paddingLeft: '1px!important',
                      },
                    }}
                    onChange={onChangeConfigurationValue}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <StrategyTime onSuccess={() => callStrategies({
            variables: {
              where: {
                status: StrategyStatus.Active,
              },
            },
          })} />
        </Grid>
        <Grid item xs={12}>
          <SymbolList strategies={strategies} />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} md={4}>
              <Grid container>
                <Grid
                  item
                  xs={textMediaQuery}
                  display='flex'
                  alignItems='center'
                >
                  <Typography paddingRight={1}>Favor Movement</Typography>
                </Grid>
                <Grid item xs={boxMediaQuery}>
                  <Box minWidth={55} position={'relative'}>
                    <TextField
                      name='favor_movement'
                      fullWidth
                      color={'success'}
                      size='small'
                      value={selected?.favor_movement}
                      inputProps={{
                        style: {
                          paddingRight: '20px',
                          paddingLeft: '1px!important',
                        },
                      }}
                      onChange={onChange}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container>
                <Grid
                  item
                  xs={textMediaQuery}
                  display='flex'
                  alignItems='center'
                >
                  <Typography paddingRight={1}>Against Movement</Typography>
                </Grid>
                <Grid item xs={boxMediaQuery}>
                  <Box minWidth={55} position={'relative'}>
                    <TextField
                      name='against_movement'
                      fullWidth
                      color={'success'}
                      size='small'
                      value={selected?.against_movement}
                      onChange={onChange}
                      InputLabelProps={{
                        shrink: true,
                        style: { fontSize: 19 },
                      }}
                      inputProps={{
                        style: {
                          paddingRight: '20px',
                          paddingLeft: '1px!important',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              sx={{ xs: 0, md: 12 }}
              md={4}
            >
              {button}
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container>
                <Grid
                  item
                  xs={textMediaQuery}
                  display='flex'
                  alignItems='center'
                >
                  <Typography paddingRight={1}>Safety Profit</Typography>
                </Grid>
                <Grid item xs={boxMediaQuery}>
                  <Box minWidth={55} position={'relative'}>
                    <TextField
                      name='safety_profit'
                      fullWidth
                      color={'success'}
                      size='small'
                      value={selected?.safety_profit}
                      inputProps={{
                        style: {
                          paddingRight: '20px',
                          paddingLeft: '1px!important',
                        },
                      }}
                      onChange={onChange}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container>
                <Grid
                  item
                  xs={textMediaQuery}
                  display='flex'
                  alignItems='center'
                >
                  <Typography paddingRight={1}>Safety Loss</Typography>
                </Grid>
                <Grid item xs={boxMediaQuery}>
                  <TextField
                    name='safety_loss'
                    fullWidth
                    color={'success'}
                    size='small'
                    value={selected?.safety_loss}
                    onChange={onChange}
                    InputLabelProps={{
                      shrink: true,
                      style: { fontSize: 19 },
                    }}
                    inputProps={{
                      style: {
                        paddingRight: '20px',
                        paddingLeft: '1px!important',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <Grid container>
                    <Grid display='flex' alignItems='center' item xs={textMediaQuery}>
                      <Typography paddingRight={1}>Cycles</Typography>
                    </Grid>
                    <Grid item xs={boxMediaQuery}>
                      <TextField
                        name='cycles'
                        fullWidth
                        color={'success'}
                        size='small'
                        value={selected?.cycles}
                        inputProps={{
                          style: {
                            paddingRight: '20px',
                            paddingLeft: '1px!important',
                          },
                        }}
                        onChange={onChange}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Grid container>
                    <Grid item display='flex' alignItems='center' xs={textMediaQuery}>
                      <Typography paddingRight={1}>Red Against</Typography>
                    </Grid>
                    <Grid item xs={boxMediaQuery}>
                      <TextField
                        name='red_against'
                        fullWidth
                        color={'success'}
                        size='small'
                        value={selected?.red_against}
                        inputProps={{
                          style: {
                            paddingRight: '20px',
                            paddingLeft: '1px!important',
                          },
                        }}
                        onChange={onChange}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
              {button}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {loadingStrategyHistories ? (
        <div>Loading...</div>
      ) : (
        <Grid
          my={'20px'}
          px={'10px'}
          container
          style={{ height: 300, maxHeight: 300, overflow: 'auto' }}
        >
          {strategyHistories?.map((strategyHistoryItem, i) => (
            <Grid key={i} xs={12} item>
              <StrategyHistoryItem
                strategyHistoryItem={strategyHistoryItem}
                active={!i}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
